import { useState, useEffect } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";
import type { IButton } from "libs/design-system/src/types/Button/button";
import {
  Gear,
  PaperPlane,
  Plus
} from "@unified-checkout/design-system/icons/index";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";
import {
  SHARED_EVENTS_PARAMS,
  SHARED_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/shared";
import {
  USER_AND_PERMISSIONS_EVENTS,
  USER_AND_PERMISSIONS_EVENTS_PARAMS,
  USER_AND_PERMISSIONS_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/user-and-permissions";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { PROFILE } from "@app/dashboard/constants/profile";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import type {
  IUserManagementHeader
} from "@app/dashboard/types/innerLayout/user_management/header";
import type {
  IRoleManagementLocationProps
} from "@app/dashboard/types/innerLayout/user_management";
import { getMerchantId, getProfileType } from "@app/dashboard/utils/localStorageUtils";
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import { getCurrentMode } from "@app/dashboard/utils/getCurrentMode";
import { getCurrentPath } from "@app/dashboard/utils/getCurrentPath";
import { getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import { Header } from "./Header";
import { UserRoleModal as InviteMemberModal } from "./components/modals/UserRoleModal";
import { UsersAndPermissions } from "./pages/UsersAndPermissions";
import { ManageRoles } from "./pages/ManageRoles";
import { RoleManagement } from "./pages/RoleManagement";
import styles from "./user-management.module.scss";


const PAGES = {
  USER_AND_PERMISSIONS: "user_and_permissions",
  MANAGE_ROLES: "manage_roles",
  ROLE_DETAILS: "role_details",
  CREATE_NEW_ROLE: "create_new_role",
};

type PageType = typeof PAGES[keyof typeof PAGES];

export const UserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLive } = useTestLiveModeStore();

  const createNewRoleMatch = useMatch(INNER_ROUTES.CREATE_NEW_ROLE);
  const roleDetailsMatch = useMatch(INNER_ROUTES.ROLE_DETAILS);
  const manageRolesMatch = useMatch(INNER_ROUTES.MANAGE_ROLES);

  const getCurrentPage = (): PageType => {
    if (createNewRoleMatch) return PAGES.CREATE_NEW_ROLE;
    if (roleDetailsMatch) return PAGES.ROLE_DETAILS;
    if (manageRolesMatch) return PAGES.MANAGE_ROLES;
    return PAGES.USER_AND_PERMISSIONS;
  };

  const currentPage = getCurrentPage();
  const profileType = getProfileType();
  const isMerchant = profileType === PROFILE.TYPE.MERCHANT;

  const [isInviteMemberModalOpen, setIsInviteMemberModalOpen] = useState(false);

  useEffect(() => {
    const merchantRole = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_ROLE);

    amplitude.track(USER_AND_PERMISSIONS_EVENTS.PAGE_VIEWED, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
      ...(
        isMerchant ?
          { [USER_AND_PERMISSIONS_EVENTS_PARAMS.ROLE]: USER_AND_PERMISSIONS_EVENTS_VALUES.MERCHANT }
          :
          merchantRole ?
            { [USER_AND_PERMISSIONS_EVENTS_PARAMS.ROLE]: merchantRole }
            :
            {}
      )
    });
  }, []);

  useEffect(() => {
    isInviteMemberModalOpen &&
      amplitude.track(USER_AND_PERMISSIONS_EVENTS.INVITE_USER_STARTED, {
        [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath(),
        [USER_AND_PERMISSIONS_EVENTS_PARAMS.INVITATION_SURFACE]: USER_AND_PERMISSIONS_EVENTS_VALUES.MODAL
      });
  }, [isInviteMemberModalOpen]);


  const handleInviteMemberSubmission = (props: {
    role?: string,
    invitedBy?: 'email' | 'phone'
  } & ({
    status: 'success',
  } | {
    status: 'error',
    failureReason?: string
  })) => {
    let amplitudeParams = {};

    const sharedParams = {
      [USER_AND_PERMISSIONS_EVENTS_PARAMS.INVITED_ROLE]: props.role,
      [USER_AND_PERMISSIONS_EVENTS_PARAMS.INVITE_CHANNEL]: props.invitedBy
    };

    if (props.status === "success") {
      amplitudeParams = {
        ...sharedParams,
        [SHARED_EVENTS_PARAMS.STATUS]: USER_AND_PERMISSIONS_EVENTS_VALUES.ACTIVE,
        [USER_AND_PERMISSIONS_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_VALUES.PENDING,
      };
    } else {
      amplitudeParams = {
        ...sharedParams,
        [USER_AND_PERMISSIONS_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_VALUES.FAILURE,
        [USER_AND_PERMISSIONS_EVENTS_PARAMS.FAILURE_REASON]: props.failureReason,
      };
    }

    amplitude.track(USER_AND_PERMISSIONS_EVENTS.INVITE_USER_SUBMITTED, amplitudeParams);
  };


  const sharedSettingsRoute = {
    text: t('innerLayout.settingsPage.breadcrumbs.settings'),
    active: true,
    onClickHandler: () => navigate(INNER_ROUTES.SETTINGS)
  };

  const sharedRoleManagementLinksProps = [
    sharedSettingsRoute,
    {
      text: t('innerLayout.settingsPage.breadcrumbs.usersAndPerms'),
      active: true,
      onClickHandler: () => navigate(INNER_ROUTES.USER_AND_PERMISSIONS)
    },
    ...(
      isAccessible(PERMISSIONS.CAN_CREATE_STAFF) ?
        [
          {
            text: t('innerLayout.userManagementPage.manageRoles'),
            active: true,
            onClickHandler: () => navigate(INNER_ROUTES.MANAGE_ROLES)
          }
        ]
        :
        []
    )
  ];

  const PAGE_HEADER: Record<string, IUserManagementHeader> = {
    [PAGES.USER_AND_PERMISSIONS]: {
      breadcrumbs: {
        linksProps: [
          sharedSettingsRoute,
          {
            text: t('innerLayout.settingsPage.breadcrumbs.usersAndPerms'),
            active: false
          }
        ]
      },
      title: t('innerLayout.userManagementPage.userAndPermissions'),
      hint: t('innerLayout.userManagementPage.manageRolesAndPermissions'),
      actionBtns: [
        ...(
          isAccessible(PERMISSIONS.CAN_CREATE_STAFF) ?
            [
              {
                size: "md",
                color: "primary",
                variant: "contained",
                leadingIcon: PaperPlane,
                text: t('innerLayout.userManagementPage.inviteMember'),
                onClickHandler: () => setIsInviteMemberModalOpen(true)
              },
              ...(
                !isMerchant ?
                  [] : [
                    {
                      size: "md",
                      color: "secondary",
                      variant: "outlined",
                      leadingIcon: Gear,
                      text: t('innerLayout.userManagementPage.manageRoles'),
                      onClickHandler: () => navigate(INNER_ROUTES.MANAGE_ROLES)
                    }
                  ]
              )
            ]
            :
            []
        ) as IButton[]
      ]
    },
    [PAGES.MANAGE_ROLES]: {
      breadcrumbs: {
        linksProps: [
          sharedSettingsRoute,
          {
            text: t('innerLayout.settingsPage.breadcrumbs.usersAndPerms'),
            active: true,
            onClickHandler: () => navigate(INNER_ROUTES.USER_AND_PERMISSIONS)
          },
          {
            text: t('innerLayout.userManagementPage.manageRoles'),
            active: false
          }
        ]
      },
      title: t('innerLayout.userManagementPage.manageRoles'),
      actionBtns: [
        ...(
          isAccessible(PERMISSIONS.CAN_CREATE_STAFF) ?
            [
              {
                size: "md",
                color: "primary",
                variant: "contained",
                leadingIcon: Plus,
                text: t('innerLayout.userManagementPage.createNewRole'),
                onClickHandler: () => navigate(INNER_ROUTES.CREATE_NEW_ROLE, {
                  state: { pageType: 'create_new_role' } as IRoleManagementLocationProps,
                })
              },
              {
                size: "md",
                color: "secondary",
                variant: "outlined",
                leadingIcon: PaperPlane,
                text: t('innerLayout.userManagementPage.inviteMember'),
                onClickHandler: () => setIsInviteMemberModalOpen(true)
              }
            ]
            :
            []
        ) as IButton[],
      ]
    },
    [PAGES.CREATE_NEW_ROLE]: {
      breadcrumbs: {
        linksProps: [
          ...sharedRoleManagementLinksProps,
          {
            text: t('innerLayout.userManagementPage.createNewRole'),
            active: false
          }
        ]
      },
      title: t('innerLayout.userManagementPage.createNewRole')
    },
    [PAGES.ROLE_DETAILS]: {
      breadcrumbs: {
        linksProps: [
          ...sharedRoleManagementLinksProps,
          {
            text: t('innerLayout.userManagementPage.roleDetails'),
            active: false
          }
        ]
      },
      title: t('innerLayout.userManagementPage.roleDetails')
    }
  };

  const {
    title,
    hint,
    actionBtns,
    breadcrumbs
  } = PAGE_HEADER[currentPage];

  const pageContent = {
    [PAGES.USER_AND_PERMISSIONS]: <UsersAndPermissions />,
    [PAGES.MANAGE_ROLES]: <ManageRoles />,
    [PAGES.CREATE_NEW_ROLE]: <RoleManagement />,
    [PAGES.ROLE_DETAILS]: <RoleManagement />
  };

  return (
    <>
      <div className={styles["user-management"]}>
        <Header
          title={title}
          hint={hint}
          actionBtns={actionBtns}
          breadcrumbs={breadcrumbs}
        />

        {pageContent[currentPage]}
      </div>

      <InviteMemberModal
        type="inviteMember"
        open={isInviteMemberModalOpen}
        setOpen={(open) => setIsInviteMemberModalOpen(open)}
        onSuccessHandler={
          ({ role, invitedBy }) =>
            handleInviteMemberSubmission({ status: 'success', role, invitedBy })
        }
        onErrorHandler={
          ({ err, role, invitedBy }) =>
            handleInviteMemberSubmission({ status: 'error', failureReason: err, role, invitedBy })
        }
      />

    </>
  )
}
